/**
 * Generated ringback tone for the browser dialer.
 *
 * The TwiML dials with `answerOnBridge`, so the SDR's leg is unanswered while the
 * prospect rings and only the carrier's early media reaches it. Carriers that send
 * none would leave the rep in silence (Twilio's comfort ringback only plays into an
 * answered leg), so we synthesize the North American pattern locally: 440+480 Hz,
 * 2s on / 4s off. useDialer stops it as soon as the carrier does send audio.
 */

const RING_ON_SECONDS = 2
const RING_OFF_SECONDS = 4
const CYCLE_SECONDS = RING_ON_SECONDS + RING_OFF_SECONDS
const CYCLES = 40 // ~4 minutes, longer than any dial we'd leave ringing
const GAIN = 0.06

// Shared and long-lived: creating/closing a context per call can disrupt OS audio
// routing for the live mic stream on macOS.
let ctx: AudioContext | null = null
let gain: GainNode | null = null
let nodes: AudioScheduledSourceNode[] = []

function _context(): AudioContext | null {
  if (ctx && ctx.state !== 'closed') return ctx
  const Ctor = window.AudioContext || (window as any).webkitAudioContext
  if (!Ctor) return null
  ctx = new Ctor() as AudioContext
  return ctx
}

export function startRingback(): void {
  if (nodes.length) return
  try {
    const audio = _context()
    if (!audio) return
    // currentTime does not advance while suspended, which would skew the envelope.
    if (audio.state === 'suspended') void audio.resume().catch(() => {})
    gain = audio.createGain()
    gain.gain.value = 0
    gain.connect(audio.destination)
    // Schedule the whole on/off envelope up front — a setInterval would drift and
    // keep running if a listener is missed.
    const start = audio.currentTime
    for (let i = 0; i < CYCLES; i++) {
      const at = start + i * CYCLE_SECONDS
      gain.gain.setValueAtTime(GAIN, at)
      gain.gain.setValueAtTime(0, at + RING_ON_SECONDS)
    }
    for (const freq of [440, 480]) {
      const osc = audio.createOscillator()
      osc.frequency.value = freq
      osc.connect(gain)
      osc.start(start)
      osc.stop(start + CYCLES * CYCLE_SECONDS)
      nodes.push(osc)
    }
  } catch (err) {
    console.warn('[ringback] could not start', err)
    // A half-built tone would otherwise play on with no handle left to stop it.
    stopRingback()
  }
}

export function stopRingback(): void {
  for (const node of nodes) {
    try {
      node.stop()
    } catch {
      /* already stopped */
    }
  }
  nodes = []
  if (gain) {
    try {
      gain.disconnect()
    } catch {
      /* already detached */
    }
    gain = null
  }
}
